import React, { useRef, createContext, useContext } from 'react';
import { useListBox, useOption, useListBoxSection, useSeparator, mergeProps } from 'react-aria';
import { useListState, Item, Section } from 'react-stately';

import { mergeRefs } from '../../lib/mergeRefs';

const defaultDisabledKeys = ['edit'];

export function renderCollectionItem(sourceItem) {
  return (
    <Item
      key={sourceItem.key}
      value={sourceItem}
      aria-label={sourceItem['aria-label']}
      textValue={sourceItem.textValue ?? '-'}
      children={sourceItem}
    />
  );
}

export function renderCollectionSection(sourceSection) {
  return (
    <Section
      key={sourceSection.key}
      title={sourceSection.title}
      aria-label={sourceSection['aria-label']}
      items={sourceSection.children}
    >
      {renderCollectionItem}
    </Section>
  );
}

export function ListBox(props) {
  const listBoxRef = useRef();

  // When props.state is passed some of these options might do nothing.
  const sharedProps = {
    id: props.id,
    items: props.items,
    children: props.renderSection != null ? renderCollectionSection : renderCollectionItem,
    label: props.label,
    'aria-label': props['aria-label'],
    'aria-labelledby': props['aria-labelledby'],
    'aria-describedby': props['aria-describedby'],
    'aria-details': props['aria-details'],
    autoFocus: props.autoFocus ?? false,
    shouldFocusWrap: true,
    selectedKeys: props.selectedKeys,
    defaultSelectedKeys: props.defaultSelectedKeys,
    disallowEmptySelection: props.disallowEmptySelection,
    disabledKeys: props.disabledKeys ?? defaultDisabledKeys,
    selectionMode: props.selectionMode ?? 'single',
    shouldFocusOnHover: props.shouldFocusOnHover ?? null,
    // needs to be true to implement drag and drop
    shouldSelectOnPressUp: props.shouldSelectOnPressUp ?? null,
    shouldUseVirtualFocus: props.shouldUseVirtualFocus ?? null,
    onSelectionChange(keys) {
      props.onSelectionChange([...keys], listState);
    },
    onFocus: props.onFocus,
    onBlur: props.onBlur,
    onFocusChange: props.onFocusChange,
  };

  // using the parent state local items and children are ignored
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const listState = props.state ?? useListState(sharedProps);
  // const collator = useCollator({ usage: 'search', sensitivity: 'base' });

  // const delegate = useMemo(() => {
  //   if (props.keyboardDelegate == null) return null;
  //
  //   return new props.keyboardDelegate(
  //     listState.collection,
  //     listState.disabledKeys,
  //     listBoxRef,
  //     collator
  //   );
  // }, [props.keyboardDelegate, listState.collection, listState.disabledKeys, listBoxRef, collator]);
  //
  // if (delegate != null) {
  //   sharedProps.keyboardDelegate = delegate;
  // }

  const listBox = useListBox(sharedProps, listState, listBoxRef);

  if (props.stateRef?.current != null) {
    props.stateRef.current = listState;
  }

  // for now it's a problem
  // rendering any input inside the listbox can cause
  // the typeahead behavior to occur and focusing whatever textvalue matches
  // since it is a nice to have delete it and deal with it later
  delete listBox.listBoxProps.onKeyDownCapture;
  const Wrapper = props.wrapper ?? React.Fragment;

  let mapper = null;

  if (props.renderSection == null) {
    mapper = (item) => {
      return (
        <ListBoxOption
          key={item.key}
          item={item}
          listState={listState}
          renderItem={props.renderItem}
        />
      );
    };
  } else {
    mapper = (item) => {
      return (
        <ListBoxSectionController
          key={item.key}
          item={item}
          listState={listState}
          sectionClassName={props.sectionClassName}
          sectionHeadingWrapperClassName={props.sectionHeadingWrapperClassName}
          sectionHeadingClassName={props.sectionHeadingClassName}
          sectionListClassName={props.sectionListClassName}
          renderSection={props.renderSection}
          renderItem={props.renderItem}
        />
      );
    };
  }

  return (
    <Wrapper>
      {sharedProps.label ? (
        props.renderLabel ? (
          props.renderLabel({
            label: sharedProps.label,
            props: listBox.labelProps,
          })
        ) : (
          <div {...listBox.labelProps}>{sharedProps.label}</div>
        )
      ) : null}
      <ul
        {...mergeProps(listBox.listBoxProps, {
          onKeyDown: props.onKeyDown,
          onKeyUp: props.onKeyUp,
        })}
        ref={mergeRefs([listBoxRef, props.listBoxRef])}
        className={props.className}
        style={props.style}
      >
        {[...listState.collection].map(mapper)}
      </ul>
    </Wrapper>
  );
}

const SectionContext = createContext();

export function useSectionContext() {
  return useContext(SectionContext);
}

function ListBoxSectionController(props) {
  const section = useListBoxSection({
    heading: props.item.rendered,
    'aria-label': props.item['aria-label'],
  });

  const separator = useSeparator({
    elementType: 'li',
  });

  // If the section is not the first, add a separator element.
  // The heading is rendered inside an <li> element, which contains
  // a <ul> with the child items.
  return (
    <SectionContext.Provider
      value={{
        section,
        item: props.item,
        value: props.item.value,
        state: props.listState,
        sectionClassName: props.sectionClassName,
        sectionHeadingWrapperClassName: props.sectionHeadingWrapperClassName,
        sectionHeadingClassName: props.sectionHeadingClassName,
        sectionListClassName: props.sectionListClassName,
        children: [...props.item.childNodes].map((node) => {
          return (
            <ListBoxOption
              key={node.key}
              item={node}
              listState={props.listState}
              renderItem={props.renderItem}
            />
          );
        }),
      }}
    >
      {props.item.key !== props.listState.collection.getFirstKey() && (
        <li {...separator.separatorProps} />
      )}

      {props.renderSection({
        item: props.item,
        value: props.item.value,
        state: props.listState,
        sectionClassName: props.sectionClassName,
        sectionHeadingWrapperClassName: props.sectionHeadingWrapperClassName,
        sectionHeadingClassName: props.sectionHeadingClassName,
        sectionListClassName: props.sectionListClassName,
      })}
    </SectionContext.Provider>
  );
}

const OptionContext = createContext();

export function useOptionContext() {
  return useContext(OptionContext);
}

function ListBoxOption(props) {
  const optionRef = useRef();
  const option = useOption(
    {
      key: props.item.key,
      'aria-label': props.item['aria-label'],
    },
    props.listState,
    optionRef,
  );

  return (
    <OptionContext.Provider
      value={{
        ref: optionRef,
        option: option,
        item: props.item,
        value: props.item.value,
        state: props.listState,
      }}
    >
      {props.renderItem({
        item: props.item,
        value: props.item.value,
        state: props.listState,
      })}
    </OptionContext.Provider>
  );
}
